import React from 'react';
import ResultRow from './ResultRow';
import type { ExploreSearchResponse } from '../../lib/exploreApi';

interface SearchResultsProps {
  data: ExploreSearchResponse | undefined;
  isLoading: boolean;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}

export default function SearchResults({ data, isLoading, page, pageSize, onPageChange }: SearchResultsProps) {
  if (isLoading) {
    return (
      <div className="explore-results">
        <div className="explore-results-loading">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="explore-skeleton-row" />
          ))}
        </div>
      </div>
    );
  }

  if (!data || data.results.length === 0) {
    return (
      <div className="explore-results">
        <div className="explore-results-empty">
          No results found. Try a different search term or category.
        </div>
      </div>
    );
  }

  const start = (page - 1) * pageSize + 1;
  const end = Math.min(start + data.results.length - 1, data.total);
  const totalPages = Math.ceil(data.total / pageSize);

  return (
    <div className="explore-results">
      <div className="explore-results-header">
        <span className="explore-results-count">
          Showing {start}&ndash;{end} of {data.total.toLocaleString()}
        </span>
      </div>

      <div className="explore-results-list">
        {data.results.map((item, idx) => (
          <ResultRow key={`${item.category}-${item.value}-${idx}`} item={item} />
        ))}
      </div>

      {totalPages > 1 && (() => {
        const pages: (number | '...')[] = [];
        const near = new Set<number>();
        near.add(1);
        near.add(totalPages);
        for (let p = page - 1; p <= page + 1; p++) {
          if (p >= 1 && p <= totalPages) near.add(p);
        }
        const sorted = Array.from(near).sort((a, b) => a - b);
        for (let i = 0; i < sorted.length; i++) {
          if (i > 0 && sorted[i] - sorted[i - 1] > 1) pages.push('...');
          pages.push(sorted[i]);
        }
        return (
          <div className="explore-pagination">
            <button className="explore-pagination-btn" disabled={page <= 1} onClick={() => onPageChange(1)} title="First page">&laquo;</button>
            <button className="explore-pagination-btn" disabled={page <= 1} onClick={() => onPageChange(page - 1)} title="Previous page">&lsaquo;</button>
            {pages.map((p, i) =>
              p === '...' ? (
                <span key={`ellipsis-${i}`} className="explore-pagination-ellipsis">&hellip;</span>
              ) : (
                <button
                  key={p}
                  className={`explore-pagination-btn${p === page ? ' explore-pagination-btn--active' : ''}`}
                  onClick={() => onPageChange(p)}
                >
                  {p}
                </button>
              )
            )}
            <button className="explore-pagination-btn" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)} title="Next page">&rsaquo;</button>
            <button className="explore-pagination-btn" disabled={page >= totalPages} onClick={() => onPageChange(totalPages)} title="Last page">&raquo;</button>
          </div>
        );
      })()}
    </div>
  );
}
